import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import UploadZone from "@/components/UploadZone";
import HistoryGrid from "@/components/HistoryGrid";
import Navbar from "@/components/Navbar";
import { Sparkles, Languages, FileText, Clock } from "lucide-react";

export default function Dashboard() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVideos = useCallback(async () => {
    try {
      const { data } = await api.get("/videos");
      setVideos(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Poll while any video is processing
  useEffect(() => {
    const anyProcessing = videos.some(
      (v) => v.status === "queued" || v.status === "extracting" || v.status === "transcribing"
    );
    if (!anyProcessing) return;
    const t = setInterval(fetchVideos, 4000);
    return () => clearInterval(t);
  }, [videos, fetchVideos]);

  const handleUploaded = (v) => {
    setVideos((prev) => [v, ...prev]);
  };

  const handleDelete = (id) => {
    setVideos((prev) => prev.filter((v) => v.id !== id));
  };

  const doneCount = videos.filter((v) => v.status === "done").length;
  const processingCount = videos.filter(
    (v) => v.status === "queued" || v.status === "extracting" || v.status === "transcribing"
  ).length;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Hero */}
        <section className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.4em] text-primary font-mono mb-4">
              // ANUTRACKS12 · SUBTITLE STUDIO
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]">
              Transcribe. Translate.
              <br />
              <span className="neon-text">Ship subtitles</span> in minutes.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-2xl">
              Upload up to <span className="font-mono text-foreground">60 minutes</span> of video.
              Get accurate, timestamped transcripts from Whisper. Translate between{" "}
              <span className="font-medium text-foreground">English</span>,{" "}
              <span className="script-ml text-foreground">മലയാളം</span>,{" "}
              <span className="script-ta text-foreground">தமிழ்</span>,{" "}
              <span className="script-te text-foreground">తెలుగు</span>, and{" "}
              <span className="script-hi text-foreground">हिन्दी</span>. Export broadcast-ready SRT.
            </p>
          </div>

          {/* Feature bento tiles */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            <FeatureTile icon={<Sparkles className="w-4 h-4" />} title="Whisper-1" desc="High-accuracy speech-to-text" />
            <FeatureTile icon={<Languages className="w-4 h-4" />} title="5 Languages" desc="EN · ML · TA · TE · HI" />
            <FeatureTile icon={<FileText className="w-4 h-4" />} title="SRT Export" desc="Timestamped subtitles" />
            <FeatureTile icon={<Clock className="w-4 h-4" />} title="Up to 1hr" desc="Long-form ready" />
          </div>
        </section>

        {/* Bento grid: upload + stats */}
        <section className="grid lg:grid-cols-12 gap-6 mb-14">
          <div className="lg:col-span-8">
            <UploadZone onUploaded={handleUploaded} />
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-3">
            <StatTile label="Total sessions" value={videos.length} testid="stat-total" />
            <StatTile label="Completed" value={doneCount} testid="stat-done" accent />
            <StatTile label="Processing" value={processingCount} testid="stat-processing" />
          </div>
        </section>

        {/* Recent history */}
        <section>
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="text-[10px] tracking-[0.3em] text-muted-foreground font-mono mb-1">
                // ARCHIVE
              </div>
              <h2 className="text-3xl font-black tracking-tighter">Recent sessions</h2>
            </div>
          </div>
          {loading ? (
            <div className="card-surface p-12 text-center text-muted-foreground font-mono text-sm animate-pulse">
              LOADING…
            </div>
          ) : (
            <HistoryGrid videos={videos.slice(0, 9)} onDelete={handleDelete} />
          )}
        </section>
      </main>
    </div>
  );
}

function FeatureTile({ icon, title, desc }) {
  return (
    <div className="card-surface p-4 hover:border-primary/40 transition-colors duration-200">
      <div className="w-8 h-8 flex items-center justify-center border border-primary/40 text-primary mb-3">
        {icon}
      </div>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
    </div>
  );
}

function StatTile({ label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`card-surface p-5 ${accent ? "border-primary/40" : ""}`}
    >
      <div className="text-[10px] font-mono tracking-widest text-muted-foreground uppercase">
        {label}
      </div>
      <div className={`mt-2 text-4xl font-black tracking-tighter ${accent ? "neon-text" : ""}`}>
        {value}
      </div>
    </div>
  );
}
